/**
 * Radio Stations Service
 * Manages radio stations from Supabase and Radio Browser API
 */

import { supabase } from '@/integrations/supabase/client';
import type { RadioStation, RadioBrowserStation } from '@/types/radioStations';

const RADIO_BROWSER_API = 'https://de1.api.radio-browser.info/json';

// Optimized radio station columns
const STATION_COLUMNS = `id, name, stream_url, favicon_url, country, tags, is_enabled, display_order, created_at, updated_at`;

/**
 * Fetch enabled radio stations from database
 */
export async function fetchRadioStations(): Promise<RadioStation[]> {
  const { data, error } = await supabase
    .from('modoforja_radio_stations')
    .select(STATION_COLUMNS)
    .eq('is_enabled', true)
    .order('display_order', { ascending: true });

  if (error) {
    console.error('Error fetching radio stations:', error);
    return [];
  }

  return data || [];
}

/**
 * Fetch all radio stations (for admin)
 */
export async function fetchAllRadioStations(): Promise<RadioStation[]> {
  const { data, error } = await supabase
    .from('modoforja_radio_stations')
    .select(STATION_COLUMNS)
    .order('display_order', { ascending: true });

  if (error) {
    console.error('Error fetching all radio stations:', error);
    return [];
  }

  return data || [];
}

/**
 * Search stations from Radio Browser API
 */
export async function searchRadioBrowserStations(
  query: string,
  searchBy: 'name' | 'tag' = 'name',
  limit: number = 20
): Promise<RadioBrowserStation[]> {
  try {
    const endpoint = searchBy === 'tag' 
      ? `${RADIO_BROWSER_API}/stations/bytag/${encodeURIComponent(query)}`
      : `${RADIO_BROWSER_API}/stations/byname/${encodeURIComponent(query)}`;
    
    const response = await fetch(`${endpoint}?limit=${limit}&order=votes&reverse=true`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch from Radio Browser API');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error searching Radio Browser:', error);
    return [];
  }
}

/**
 * Get popular stations by tag from Radio Browser API
 */
export async function getPopularStationsByTag(
  tag: string,
  limit: number = 10
): Promise<RadioBrowserStation[]> {
  try {
    const response = await fetch(
      `${RADIO_BROWSER_API}/stations/bytag/${encodeURIComponent(tag)}?limit=${limit}&order=votes&reverse=true&hidebroken=true`
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch popular stations');
    }
    
    return response.json();
  } catch (error) {
    console.error('Error fetching popular stations:', error);
    return [];
  }
}

/**
 * Add a radio station to the database
 */
export async function addRadioStation(station: Omit<RadioStation, 'id'>): Promise<RadioStation | null> {
  const { data, error } = await supabase
    .from('modoforja_radio_stations')
    .insert({
      name: station.name,
      stream_url: station.stream_url,
      favicon_url: station.favicon_url,
      country: station.country,
      tags: station.tags,
      is_enabled: station.is_enabled,
      display_order: station.display_order,
    })
    .select()
    .single();

  if (error) {
    console.error('Error adding radio station:', error);
    return null;
  }

  return data;
}

/**
 * Toggle station enabled status
 */
export async function toggleStationEnabled(id: string, isEnabled: boolean): Promise<boolean> {
  const { error } = await supabase
    .from('modoforja_radio_stations')
    .update({ is_enabled: isEnabled })
    .eq('id', id);

  if (error) {
    console.error('Error toggling station:', error);
    return false;
  }

  return true;
}

/**
 * Delete a radio station
 */
export async function deleteRadioStation(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('modoforja_radio_stations')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting station:', error);
    return false;
  }

  return true;
}

/**
 * Update station order
 */
export async function updateStationOrder(id: string, displayOrder: number): Promise<boolean> {
  const { error } = await supabase
    .from('modoforja_radio_stations')
    .update({ display_order: displayOrder })
    .eq('id', id);

  if (error) {
    console.error('Error updating station order:', error);
    return false;
  }

  return true;
}
